import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminExecutiveAnalytics.css";

type MetricDefinition={
  key:string;
  label:string;
  numerator:string;
  denominator:string;
  timeBasis:string;
  note?:string;
};

type TrendRow={
  month:string;
  assessmentAssigned:number;
  assessmentCompleted:number;
  badgeIssued:number;
  badgeClaimed:number;
  importRequestedRows:number;
  importFailedRows:number;
  reportSubmissions:number;
  supportResolved:number;
};

type SchoolRow={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  yearCode?:string|null;
  activeStudents:number;
  operationalReady:boolean;
  assessmentAssigned:number;
  assessmentCompleted:number;
  assessmentCompletionRate?:number|null;
  badgeIssued:number;
  badgeClaimed:number;
  badgeClaimRate?:number|null;
  importRequestedRows:number;
  importFailedRows:number;
  importSuccessRate?:number|null;
  reportSubmissionCount:number;
  reportApprovedCount:number;
  reportReturnedCount:number;
  supportStatus?:string|null;
  supportUpdatedAt?:string|null;
  inRollout:boolean;
  rolloutInviteClaimed:boolean;
};

type SnapshotRow={
  snapshotId:string;
  title:string;
  periodStart:string;
  periodEnd:string;
  createdAt:string;
  createdBy:string;
};

type Analytics={
  scope:{
    rootOrganizationId:string;
    rootName:string;
    rootType:string;
    periodStart:string;
    periodEnd:string;
    schoolCount:number;
  };
  summary:{
    schoolCount:number;
    activeStudents:number;
    operationalReadySchools:number;
    operationalReadinessRate?:number|null;
    assessmentAssigned:number;
    assessmentCompleted:number;
    assessmentCompletionRate?:number|null;
    badgeIssued:number;
    badgeClaimed:number;
    badgeClaimRate?:number|null;
    importRequestedRows:number;
    importSuccessfulRows:number;
    importSuccessRate?:number|null;
    reportSubmittedSchools:number;
    reportCoverageRate?:number|null;
    supportMonitoringSchools:number;
    supportingSchools:number;
    resolvedSupportCases:number;
    rolloutSchools:number;
    rolloutClaimedSchools:number;
    rolloutClaimRate?:number|null;
  };
  definitions:MetricDefinition[];
  trend:TrendRow[];
  schools:SchoolRow[];
  snapshots:SnapshotRow[];
};

type SnapshotDetail={
  snapshotId:string;
  title:string;
  periodStart:string;
  periodEnd:string;
  createdAt:string;
  createdBy:string;
  payload:Omit<Analytics,"snapshots">;
};

type TrendMode="ASSESSMENT"|"BADGE"|"IMPORT"|"REPORT";

const TODAY=new Date().toISOString().slice(0,10);

function isoDateOffset(days:number){
  const date=new Date();
  date.setDate(date.getDate()-days);
  return date.toISOString().slice(0,10);
}

function yearStart(){
  return TODAY.slice(0,4)+"-01-01";
}

function rate(value?:number|null){
  return value==null ? "N/A" : value.toLocaleString("ko-KR",{maximumFractionDigits:1})+"%";
}

function number(value:number){
  return value.toLocaleString("ko-KR");
}

function date(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function supportLabel(value?:string|null){
  if(value==="MONITORING")return "모니터링";
  if(value==="SUPPORTING")return "지원 중";
  if(value==="RESOLVED")return "해결";
  return "-";
}

function csvCell(value:unknown){
  const text=value==null?"":String(value);
  return '"'+text.replace(/"/g,'""')+'"';
}

function downloadText(filename:string,content:string,type:string){
  const blob=new Blob(["\uFEFF",content],{type});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;
  a.download=filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(rows:Array<Record<string,unknown>>,filename:string){
  if(!rows.length)return;
  const headers=Object.keys(rows[0]);
  const body=[
    headers.map(csvCell).join(","),
    ...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))
  ].join("\r\n");
  downloadText(filename,body,"text/csv;charset=utf-8");
}

export function AdminExecutiveAnalytics({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [periodStart,setPeriodStart]=useState(yearStart());
  const [periodEnd,setPeriodEnd]=useState(TODAY);
  const [data,setData]=useState<Analytics|null>(null);
  const [trendMode,setTrendMode]=useState<TrendMode>("BADGE");
  const [schoolQuery,setSchoolQuery]=useState("");
  const [readinessFilter,setReadinessFilter]=useState("ALL");
  const [snapshotTitle,setSnapshotTitle]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_executive_analytics",{
      p_root_organization_id:rootOrganizationId,
      p_period_start:periodStart,
      p_period_end:periodEnd
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setData(data as Analytics);
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,periodStart,periodEnd,refresh]);

  const visibleSchools=useMemo(()=>{
    const keyword=schoolQuery.trim().toLowerCase();
    return (data?.schools || []).filter((school)=>{
      if(readinessFilter==="READY" && !school.operationalReady)return false;
      if(readinessFilter==="ATTENTION" && school.operationalReady)return false;
      return !keyword || [school.schoolName,school.schoolCode || "",school.yearCode || ""]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,schoolQuery,readinessFilter]);

  function setPreset(days:"YTD"|90|180|365){
    setPeriodEnd(TODAY);
    setPeriodStart(days==="YTD" ? yearStart() : isoDateOffset(days-1));
  }

  async function createSnapshot(){
    if(!data)return;
    setBusy("snapshot");
    setError("");
    setMessage("");
    const {data:result,error}=await client.rpc("admin_create_executive_snapshot",{
      p_root_organization_id:rootOrganizationId,
      p_period_start:periodStart,
      p_period_end:periodEnd,
      p_title:snapshotTitle.trim() || null
    });
    setBusy("");
    if(error){setError(error.message);return;}
    const value=result as {title:string};
    setSnapshotTitle("");
    setMessage(`Executive Snapshot 저장 완료 · ${value.title}`);
    setRefresh((value)=>value+1);
  }

  async function downloadSnapshot(snapshot:SnapshotRow){
    setBusy("snapshot-download:"+snapshot.snapshotId);
    setError("");
    const {data,error}=await client.rpc("admin_executive_snapshot_detail",{
      p_root_organization_id:rootOrganizationId,
      p_snapshot_id:snapshot.snapshotId
    });
    setBusy("");
    if(error){setError(error.message);return;}
    const detail=data as SnapshotDetail;
    downloadText(
      `RS_CAREER_${snapshot.periodStart}_${snapshot.periodEnd}_executive_snapshot.json`,
      JSON.stringify(detail,null,2),
      "application/json;charset=utf-8"
    );
  }

  function exportSummary(){
    if(!data)return;
    const s=data.summary;
    downloadCsv([{
      root:data.scope.rootName,
      periodStart:data.scope.periodStart,
      periodEnd:data.scope.periodEnd,
      schoolCount:s.schoolCount,
      activeStudents:s.activeStudents,
      operationalReadySchools:s.operationalReadySchools,
      operationalReadinessRate:s.operationalReadinessRate ?? "",
      assessmentAssigned:s.assessmentAssigned,
      assessmentCompleted:s.assessmentCompleted,
      assessmentCompletionRate:s.assessmentCompletionRate ?? "",
      badgeIssued:s.badgeIssued,
      badgeClaimed:s.badgeClaimed,
      badgeClaimRate:s.badgeClaimRate ?? "",
      importRequestedRows:s.importRequestedRows,
      importSuccessfulRows:s.importSuccessfulRows,
      importSuccessRate:s.importSuccessRate ?? "",
      reportSubmittedSchools:s.reportSubmittedSchools,
      reportCoverageRate:s.reportCoverageRate ?? "",
      supportMonitoringSchools:s.supportMonitoringSchools,
      supportingSchools:s.supportingSchools,
      resolvedSupportCases:s.resolvedSupportCases,
      rolloutSchools:s.rolloutSchools,
      rolloutClaimedSchools:s.rolloutClaimedSchools,
      rolloutClaimRate:s.rolloutClaimRate ?? ""
    }],`RS_CAREER_${periodStart}_${periodEnd}_executive_summary.csv`);
  }

  function exportSchools(){
    if(!data)return;
    downloadCsv(data.schools.map((school)=>({
      schoolCode:school.schoolCode || "",
      schoolName:school.schoolName,
      yearCode:school.yearCode || "",
      activeStudents:school.activeStudents,
      operationalReady:school.operationalReady?"Y":"N",
      assessmentAssigned:school.assessmentAssigned,
      assessmentCompleted:school.assessmentCompleted,
      assessmentCompletionRate:school.assessmentCompletionRate ?? "",
      badgeIssued:school.badgeIssued,
      badgeClaimed:school.badgeClaimed,
      badgeClaimRate:school.badgeClaimRate ?? "",
      importRequestedRows:school.importRequestedRows,
      importFailedRows:school.importFailedRows,
      importSuccessRate:school.importSuccessRate ?? "",
      reportSubmissionCount:school.reportSubmissionCount,
      reportApprovedCount:school.reportApprovedCount,
      reportReturnedCount:school.reportReturnedCount,
      supportStatus:school.supportStatus || "",
      inRollout:school.inRollout?"Y":"N",
      rolloutInviteClaimed:school.rolloutInviteClaimed?"Y":"N"
    })),`RS_CAREER_${periodStart}_${periodEnd}_school_comparison.csv`);
  }

  function exportTrend(){
    if(!data)return;
    downloadCsv(data.trend.map((row)=>({...row})),`RS_CAREER_${periodStart}_${periodEnd}_monthly_trend.csv`);
  }

  if(!data)return <p className="muted">성과 분석 데이터를 불러오는 중…</p>;

  const s=data.summary;

  return (
    <section className="executive-analytics-page">
      <header className="executive-analytics-hero">
        <div>
          <p className="eyebrow">ANALYTICS, KPI & EXECUTIVE REPORTING CENTER</p>
          <h2>성과 분석·Executive Reporting</h2>
          <p>
            학교·학생·검사·Life Stamp·Import·지원·공식보고 데이터를 같은 Scope와 기간 기준으로
            비교하고, 재현 가능한 Executive Snapshot으로 저장합니다.
          </p>
        </div>
        <div className="executive-scope-chip">
          <span>REPORTING SCOPE</span>
          <strong>{data.scope.rootName}</strong>
          <small>{data.scope.periodStart} ~ {data.scope.periodEnd}</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <section className="analytics-period-bar">
        <div className="analytics-presets">
          <button onClick={()=>setPreset("YTD")}>올해</button>
          <button onClick={()=>setPreset(90)}>90일</button>
          <button onClick={()=>setPreset(180)}>180일</button>
          <button onClick={()=>setPreset(365)}>365일</button>
        </div>
        <label>시작<input type="date" value={periodStart} max={periodEnd} onChange={(event)=>setPeriodStart(event.target.value)} /></label>
        <label>종료<input type="date" value={periodEnd} min={periodStart} max={TODAY} onChange={(event)=>setPeriodEnd(event.target.value)} /></label>
        <div className="analytics-export-actions">
          <button className="secondary" onClick={exportSummary}>요약 CSV</button>
          <button className="secondary" onClick={exportSchools}>학교 CSV</button>
          <button className="secondary" onClick={exportTrend}>추이 CSV</button>
        </div>
      </section>

      <div className="executive-kpi-grid">
        <Kpi label="활성 학교" value={number(s.schoolCount)} sub={`학생 ${number(s.activeStudents)}명`} />
        <Kpi label="운영준비율" value={rate(s.operationalReadinessRate)} sub={`${s.operationalReadySchools}/${s.schoolCount}개교`} tone={s.operationalReadinessRate!=null && s.operationalReadinessRate<100?"warn":"normal"} />
        <Kpi label="검사 완료율" value={rate(s.assessmentCompletionRate)} sub={`${s.assessmentCompleted}/${s.assessmentAssigned}건`} />
        <Kpi label="Life Stamp 수령률" value={rate(s.badgeClaimRate)} sub={`${s.badgeClaimed}/${s.badgeIssued}건`} tone={s.badgeIssued>0 && s.badgeClaimRate!==100?"warn":"normal"} />
        <Kpi label="Import 성공률" value={rate(s.importSuccessRate)} sub={`${s.importSuccessfulRows}/${s.importRequestedRows}행`} />
        <Kpi label="보고서 제출 커버리지" value={rate(s.reportCoverageRate)} sub={`${s.reportSubmittedSchools}/${s.schoolCount}개교`} />
        <Kpi label="지원 진행" value={number(s.supportMonitoringSchools+s.supportingSchools)} sub={`해결 ${s.resolvedSupportCases}건`} />
        <Kpi label="Rollout Claim률" value={rate(s.rolloutClaimRate)} sub={`${s.rolloutClaimedSchools}/${s.rolloutSchools}개교`} />
      </div>

      <div className="executive-analytics-main">
        <section className="analytics-card trend-card">
          <div className="analytics-card-head">
            <div><p className="eyebrow">MONTHLY TREND</p><h3>월별 운영 추이</h3></div>
            <div className="trend-mode-tabs">
              {([
                ["BADGE","Life Stamp"],
                ["ASSESSMENT","검사"],
                ["IMPORT","Import"],
                ["REPORT","보고·지원"]
              ] as Array<[TrendMode,string]>).map(([value,label])=>(
                <button key={value} className={trendMode===value?"active":""} onClick={()=>setTrendMode(value)}>{label}</button>
              ))}
            </div>
          </div>
          <TrendChart rows={data.trend} mode={trendMode} />
        </section>

        <section className="analytics-card executive-funnel-card">
          <div className="analytics-card-head"><div><p className="eyebrow">COVERAGE</p><h3>운영 커버리지</h3></div></div>
          <CoverageRow label="운영준비 학교" numerator={s.operationalReadySchools} denominator={s.schoolCount} value={s.operationalReadinessRate} />
          <CoverageRow label="공식보고 제출학교" numerator={s.reportSubmittedSchools} denominator={s.schoolCount} value={s.reportCoverageRate} />
          <CoverageRow label="Rollout 교사 Claim" numerator={s.rolloutClaimedSchools} denominator={s.rolloutSchools} value={s.rolloutClaimRate} />
          <div className="executive-activity-summary">
            <div><span>검사 배정</span><strong>{number(s.assessmentAssigned)}</strong></div>
            <div><span>배지 발급</span><strong>{number(s.badgeIssued)}</strong></div>
            <div><span>Import 행</span><strong>{number(s.importRequestedRows)}</strong></div>
            <div><span>지원 해결</span><strong>{number(s.resolvedSupportCases)}</strong></div>
          </div>
        </section>
      </div>

      <section className="analytics-card">
        <div className="analytics-card-head school-comparison-head">
          <div>
            <p className="eyebrow">SCHOOL COMPARISON</p>
            <h3>학교별 KPI 비교</h3>
          </div>
          <div className="school-comparison-tools">
            <input value={schoolQuery} onChange={(event)=>setSchoolQuery(event.target.value)} placeholder="학교명·코드 검색" />
            <select value={readinessFilter} onChange={(event)=>setReadinessFilter(event.target.value)}>
              <option value="ALL">전체 학교</option>
              <option value="READY">운영준비</option>
              <option value="ATTENTION">준비 필요</option>
            </select>
          </div>
        </div>

        <div className="executive-school-table">
          <div className="executive-school-row head">
            <span>학교</span><span>학생</span><span>운영</span><span>검사</span><span>배지</span><span>Import</span><span>보고</span><span>지원</span>
          </div>
          {visibleSchools.map((school)=>(
            <div className="executive-school-row" key={school.schoolId}>
              <span><strong>{school.schoolName}</strong><small>{school.schoolCode || "-"} · {school.yearCode || "-"}</small></span>
              <span>{number(school.activeStudents)}</span>
              <span className={school.operationalReady?"status-good":"status-attention"}>{school.operationalReady?"준비":"확인"}</span>
              <span><b>{rate(school.assessmentCompletionRate)}</b><small>{school.assessmentCompleted}/{school.assessmentAssigned}</small></span>
              <span><b>{rate(school.badgeClaimRate)}</b><small>{school.badgeClaimed}/{school.badgeIssued}</small></span>
              <span><b>{rate(school.importSuccessRate)}</b><small>{school.importRequestedRows-school.importFailedRows}/{school.importRequestedRows}</small></span>
              <span><b>{school.reportSubmissionCount}</b><small>승인 {school.reportApprovedCount} · 반려 {school.reportReturnedCount}</small></span>
              <span>{supportLabel(school.supportStatus)}</span>
            </div>
          ))}
          {!visibleSchools.length && <p className="case-empty">조건에 맞는 학교가 없습니다.</p>}
        </div>
      </section>

      <div className="executive-analytics-main lower">
        <section className="analytics-card">
          <div className="analytics-card-head"><div><p className="eyebrow">METRIC GOVERNANCE</p><h3>KPI 정의·분모</h3></div></div>
          <div className="metric-definition-list">
            {data.definitions.map((definition)=>(
              <article key={definition.key}>
                <strong>{definition.label}</strong>
                <div><span>분자</span><p>{definition.numerator}</p></div>
                <div><span>분모</span><p>{definition.denominator}</p></div>
                <div><span>시점</span><p>{definition.timeBasis}</p></div>
                {definition.note && <small>{definition.note}</small>}
              </article>
            ))}
          </div>
        </section>

        <section className="analytics-card snapshot-card">
          <div className="analytics-card-head"><div><p className="eyebrow">EXECUTIVE SNAPSHOT</p><h3>보고 기준점 저장</h3></div></div>
          <p className="snapshot-description">
            현재 기간의 KPI·월별 추이·학교 비교를 JSON Snapshot으로 고정합니다. 이후 원천데이터가 바뀌어도 당시 보고값을 재현할 수 있습니다.
          </p>
          <div className="snapshot-create">
            <input value={snapshotTitle} onChange={(event)=>setSnapshotTitle(event.target.value)} placeholder={`${data.scope.rootName} Executive KPI`} />
            <button className="primary" disabled={busy!==""} onClick={createSnapshot}>{busy==="snapshot"?"저장 중…":"Snapshot 저장"}</button>
          </div>
          <div className="snapshot-list">
            {data.snapshots.map((snapshot)=>(
              <div key={snapshot.snapshotId}>
                <span><strong>{snapshot.title}</strong><small>{snapshot.periodStart} ~ {snapshot.periodEnd} · {date(snapshot.createdAt)} · {snapshot.createdBy}</small></span>
                <button className="secondary" disabled={busy!==""} onClick={()=>downloadSnapshot(snapshot)}>
                  {busy==="snapshot-download:"+snapshot.snapshotId?"준비 중…":"JSON"}
                </button>
              </div>
            ))}
            {!data.snapshots.length && <p className="case-empty">저장된 Executive Snapshot이 없습니다.</p>}
          </div>
        </section>
      </div>
    </section>
  );
}

function Kpi({
  label,value,sub,tone="normal"
}:{label:string;value:string;sub:string;tone?:"normal"|"warn"}){
  return <div className={"executive-kpi "+tone}><span>{label}</span><strong>{value}</strong><small>{sub}</small></div>;
}

function CoverageRow({
  label,numerator,denominator,value
}:{label:string;numerator:number;denominator:number;value?:number|null}){
  const width=value==null?0:Math.min(100,Math.max(0,value));
  return (
    <div className="coverage-row">
      <div><span>{label}</span><strong>{rate(value)}</strong></div>
      <div className="coverage-track"><i style={{width:width+"%"}} /></div>
      <small>{numerator}/{denominator}</small>
    </div>
  );
}

function TrendChart({rows,mode}:{rows:TrendRow[];mode:TrendMode}){
  const series=mode==="ASSESSMENT"
    ? [
        {key:"assessmentAssigned" as keyof TrendRow,label:"배정"},
        {key:"assessmentCompleted" as keyof TrendRow,label:"완료"}
      ]
    : mode==="BADGE"
      ? [
          {key:"badgeIssued" as keyof TrendRow,label:"발급"},
          {key:"badgeClaimed" as keyof TrendRow,label:"수령"}
        ]
      : mode==="IMPORT"
        ? [
            {key:"importRequestedRows" as keyof TrendRow,label:"요청행"},
            {key:"importFailedRows" as keyof TrendRow,label:"실패행"}
          ]
        : [
            {key:"reportSubmissions" as keyof TrendRow,label:"보고서"},
            {key:"supportResolved" as keyof TrendRow,label:"지원해결"}
          ];

  const values=rows.flatMap((row)=>series.map((item)=>Number(row[item.key] || 0)));
  const max=Math.max(1,...values);
  const width=760;
  const height=240;
  const left=38;
  const top=18;
  const bottom=42;
  const innerWidth=width-left-12;
  const innerHeight=height-top-bottom;
  const x=(index:number)=>left+(rows.length<=1?innerWidth/2:index*innerWidth/(rows.length-1));
  const y=(value:number)=>top+innerHeight-(value/max)*innerHeight;

  return (
    <div className="trend-chart-wrap">
      <div className="trend-legend">{series.map((item,index)=><span key={String(item.key)}><i className={"series-"+index} />{item.label}</span>)}</div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="월별 KPI 추이">
        <line x1={left} y1={top+innerHeight} x2={width-12} y2={top+innerHeight} className="chart-axis" />
        {[0,.25,.5,.75,1].map((ratio)=>(
          <g key={ratio}>
            <line x1={left} y1={top+innerHeight-innerHeight*ratio} x2={width-12} y2={top+innerHeight-innerHeight*ratio} className="chart-grid" />
            <text x={left-8} y={top+innerHeight-innerHeight*ratio+4} textAnchor="end" className="chart-label">{Math.round(max*ratio)}</text>
          </g>
        ))}
        {series.map((item,seriesIndex)=>{
          const points=rows.map((row,index)=>`${x(index)},${y(Number(row[item.key] || 0))}`).join(" ");
          return (
            <g key={String(item.key)} className={"trend-series series-"+seriesIndex}>
              <polyline points={points} fill="none" />
              {rows.map((row,index)=>(
                <circle key={row.month} cx={x(index)} cy={y(Number(row[item.key] || 0))} r="3.5">
                  <title>{row.month} · {item.label} {Number(row[item.key] || 0)}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {rows.map((row,index)=>(
          <text key={row.month} x={x(index)} y={height-15} textAnchor="middle" className="chart-label month-label">
            {row.month.slice(5)}
          </text>
        ))}
      </svg>
      {!values.some((value)=>value>0) && <div className="trend-empty">선택 기간에 해당 지표 실적이 없습니다.</div>}
    </div>
  );
}
